import { createShellStore, type ShellStoreApi, type WorkShapes } from "@hakili/app-shell";

import { cheminDAtterrissage, VUES, type Vue } from "./views.js";

/**
 * `P0-UIK-014`: the console's shell store.
 *
 * 4.6.5's rule , per-view and per-dossier work state lives in one store and
 * never in a screen , with the console's own persona union: the five views.
 * `WF-CON-0` adds the console-specific half: *"on a view change, fall back to
 * the view's first screen without losing saved work."* The falling back is the
 * guard's job; the NOT LOSING is this store's, and it is the harder half
 * because nothing announces its failure.
 *
 * **`sessionStorage`, per tab, deliberately.** 4.6.5 gives `localStorage` to
 * the client app only, *"because a client loses his connection mid-form and
 * comes back on the same handset"*. A console user is a staff member on a
 * shared desktop, and a chargé de dossier's half-written note surviving into
 * the next person's session is a disclosure, not a convenience.
 */

/** What a console user can have in flight on one dossier. Grows as screens arrive. */
export interface ConsoleWork extends WorkShapes {
  readonly dossier: {
    /** A note being typed in the conversation panel, per dossier. */
    brouillonMessage: string;
    /** A reassignment being considered but not yet confirmed. */
    affectationEnvisagee: string;
  };
  readonly vehicle: { commentaire: string };
  readonly partner: { brouillonMessage: string };
}

export type ConsoleStore = ShellStoreApi<Vue, ConsoleWork>;

const sessionStorageOrMemory = (): Storage => {
  // A store built during a test or a server render has no `sessionStorage`,
  // and throwing there would make the console unrenderable outside a browser
  // for no benefit: an in-memory map behaves identically for one session.
  if (typeof globalThis.sessionStorage !== "undefined") return globalThis.sessionStorage;
  const seen = new Map<string, string>();
  return {
    getItem: (name: string) => seen.get(name) ?? null,
    setItem: (name: string, value: string) => {
      seen.set(name, value);
    },
    removeItem: (name: string) => {
      seen.delete(name);
    },
    clear: () => {
      seen.clear();
    },
    key: (index: number) => [...seen.keys()][index] ?? null,
    get length() {
      return seen.size;
    },
  };
};

export function creerConsoleStore(vue: Vue): ConsoleStore {
  return createShellStore<Vue, ConsoleWork>({
    personas: VUES,
    initialPersona: vue,
    // Each view lands on its own first screen, so a `demo` switch to another
    // view arrives somewhere that view is allowed to be.
    landingScreen: cheminDAtterrissage,
    storage: sessionStorageOrMemory(),
    name: "hakili.console",
  });
}
